import { Link, useLocation } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { useEffect, useState } from "react";
import { fetchStreak } from "../services/api";

function Navbar({ user, setUser, onConnectLeetCode, streakVersion = 0 }) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [streak, setStreak] = useState(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  const leetcodeConnected = localStorage.getItem("roundprep_lc_synced") === "true";
  const lcUsername = localStorage.getItem("lc_username") || null;

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (user) {
      fetchStreak(user.uid)
        .then((d) => setStreak(d.streak))
        .catch(() => setStreak(null));
    } else {
      setStreak(null);
    }
  }, [user, streakVersion]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem("user");
      setUser(null);
      setMenuOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/contest-drill", label: "Contest Drill" },
    { to: "/rating-lab", label: "Rating Lab" },
  ];

  return (
    <nav
      style={{
        position: "sticky",
        top: 0,
        zIndex: 50,
        background: "rgba(3,4,10,0.88)",
        backdropFilter: "blur(24px) saturate(180%)",
        WebkitBackdropFilter: "blur(24px) saturate(180%)",
        borderBottom: "1px solid rgba(255,255,255,0.07)",
      }}
    >
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "0 28px",
          height: 54,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 24,
        }}
      >
        {/* LEFT: Brand */}
        <Link
          to="/"
          style={{
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              boxShadow: "0 0 12px rgba(96,165,250,0.3)",
            }}
          >
            ⚡
          </div>
          <span
            style={{
              fontSize: 15.5,
              fontWeight: 700,
              fontFamily: "var(--font-display)",
              color: "#e2e8f0",
              letterSpacing: "-0.025em",
            }}
          >
            RoundPrep
          </span>
        </Link>

        {/* CENTER: Nav links (desktop) */}
        {isDesktop && (
          <div
            style={{ display: "flex", alignItems: "center", gap: 2, flex: 1, justifyContent: "center" }}
          >
            {navLinks.map(({ to, label }) => {
              const active = isActive(to);
              return (
                <Link
                  key={to}
                  to={to}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 8,
                    fontSize: 13.5,
                    fontWeight: active ? 600 : 400,
                    fontFamily: "var(--font-display)",
                    color: active ? "#e2e8f0" : "rgba(148,163,184,0.65)",
                    background: active ? "rgba(255,255,255,0.08)" : "transparent",
                    textDecoration: "none",
                    transition: "all 0.15s ease",
                    whiteSpace: "nowrap",
                    letterSpacing: "-0.01em",
                    position: "relative",
                  }}
                  onMouseEnter={e => {
                    if (!active) e.currentTarget.style.color = "rgba(203,213,225,0.9)";
                  }}
                  onMouseLeave={e => {
                    if (!active) e.currentTarget.style.color = "rgba(148,163,184,0.65)";
                  }}
                >
                  {label}
                  {active && (
                    <span
                      style={{
                        position: "absolute",
                        bottom: -1,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 16,
                        height: 2,
                        borderRadius: 2,
                        background: "linear-gradient(90deg, #60a5fa, #c084fc)",
                      }}
                    />
                  )}
                </Link>
              );
            })}
          </div>
        )}

        {/* RIGHT: Streak + User */}
        {isDesktop && (
          <div
            style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}
          >
            {user && streak !== null && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "4px 10px",
                  borderRadius: 999,
                  background: streak > 0 ? "rgba(249,115,22,0.1)" : "rgba(255,255,255,0.04)",
                  border: streak > 0 ? "1px solid rgba(249,115,22,0.22)" : "1px solid rgba(255,255,255,0.07)",
                  fontSize: 12.5,
                  fontFamily: "var(--font-display)",
                  fontWeight: 600,
                  color: streak > 0 ? "#fb923c" : "rgba(148,163,184,0.45)",
                  letterSpacing: "-0.01em",
                  userSelect: "none",
                }}
                title={`${streak}-day streak`}
              >
                <span style={{ fontSize: 12, filter: streak > 0 ? "none" : "grayscale(1)", opacity: streak > 0 ? 1 : 0.5 }}>🔥</span>
                {streak}
              </div>
            )}

            {user && onConnectLeetCode && (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {/* LeetCode username pill — shown when connected */}
                {leetcodeConnected && lcUsername && (
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "4px 10px",
                    borderRadius: 8,
                    background: "rgba(255,161,22,0.08)",
                    border: "1px solid rgba(255,161,22,0.2)",
                    fontSize: 12,
                    fontFamily: "var(--font-display)",
                    fontWeight: 500,
                    color: "#ffa116",
                    letterSpacing: "-0.01em",
                    whiteSpace: "nowrap",
                  }}>
                    {/* LeetCode icon */}
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M13.483 0a1.374 1.374 0 0 0-.961.438L7.116 6.226l-3.854 4.126a5.266 5.266 0 0 0-1.209 2.104 5.35 5.35 0 0 0-.125.513 5.527 5.527 0 0 0 .062 2.362 5.83 5.83 0 0 0 .349 1.017 5.938 5.938 0 0 0 1.271 1.818l4.277 4.193.039.038c2.248 2.165 5.852 2.133 8.063-.074l2.396-2.392c.54-.54.54-1.414.003-1.955a1.378 1.378 0 0 0-1.951-.003l-2.396 2.392a3.021 3.021 0 0 1-4.205.038l-.02-.019-4.276-4.193c-.652-.64-.972-1.469-.948-2.263a2.68 2.68 0 0 1 .066-.523 2.545 2.545 0 0 1 .619-1.164L9.13 8.114c1.058-1.134 3.204-1.27 4.43-.278l3.501 2.831c.593.48 1.461.387 1.94-.207a1.384 1.384 0 0 0-.207-1.943l-3.5-2.831c-.8-.647-1.766-1.045-2.774-1.202l2.015-2.158A1.384 1.384 0 0 0 13.483 0zm-2.866 12.815a1.38 1.38 0 0 0-1.38 1.382 1.38 1.38 0 0 0 1.38 1.382H20.79a1.38 1.38 0 0 0 1.38-1.382 1.38 1.38 0 0 0-1.38-1.382z" />
                    </svg>
                    {lcUsername}
                  </div>
                )}

                {/* Connect / Sync button */}
                <button
                  onClick={onConnectLeetCode}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "5px 11px",
                    borderRadius: 8,
                    fontSize: 12.5,
                    fontFamily: "var(--font-display)",
                    fontWeight: 600,
                    color: leetcodeConnected ? "#2dd4bf" : "#60a5fa",
                    background: "transparent",
                    border: leetcodeConnected ? "1px solid rgba(45,212,191,0.25)" : "1px solid rgba(96,165,250,0.25)",
                    cursor: "pointer",
                    letterSpacing: "-0.01em",
                    transition: "all 0.15s",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = leetcodeConnected ? "rgba(45,212,191,0.08)" : "rgba(96,165,250,0.08)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                  title={leetcodeConnected ? "Sync your latest LeetCode solved problems & contest data" : "Connect your LeetCode account to personalise your dashboard"}
                >
                  {leetcodeConnected ? (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="23 4 23 10 17 10" />
                      <polyline points="1 20 1 14 7 14" />
                      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                    </svg>
                  ) : (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                  )}
                  {leetcodeConnected ? "Sync" : "Connect LeetCode"}
                </button>
              </div>
            )}

            {user ? (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "5px 11px",
                    borderRadius: 999,
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.09)",
                    fontSize: 12.5,
                    fontFamily: "var(--font-display)",
                    color: "rgba(203,213,225,0.85)",
                    letterSpacing: "-0.01em",
                  }}
                >
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #3b82f6, #8b5cf6)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#fff",
                      flexShrink: 0,
                    }}
                  >
                    {user.name[0].toUpperCase()}
                  </div>
                  {user.name.split(" ")[0]}
                </div>

                <button
                  onClick={handleLogout}
                  style={{
                    padding: "5px 13px",
                    borderRadius: 8,
                    fontSize: 12.5,
                    fontFamily: "var(--font-display)",
                    fontWeight: 500,
                    color: "rgba(148,163,184,0.6)",
                    background: "transparent",
                    border: "1px solid rgba(255,255,255,0.07)",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    letterSpacing: "-0.01em",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.color = "#f87171";
                    e.currentTarget.style.borderColor = "rgba(239,68,68,0.25)";
                    e.currentTarget.style.background = "rgba(239,68,68,0.06)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.color = "rgba(148,163,184,0.6)";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  Sign out
                </button>
              </>
            ) : (
              <span style={{ fontSize: 12, color: "rgba(100,116,139,0.5)", fontFamily: "var(--font-display)" }}>
                Sign in below ↓
              </span>
            )}
          </div>
        )}

        {/* Hamburger (mobile) */}
        {!isDesktop && (
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.08)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 4.5,
              cursor: "pointer",
              padding: 0,
            }}
            aria-label="Menu"
          >
            <span style={{ display: "block", width: 15, height: 1.5, background: "rgba(203,213,225,0.7)", borderRadius: 2, transition: "all 0.2s", transform: menuOpen ? "rotate(45deg) translate(4px, 4px)" : "none" }} />
            <span style={{ display: "block", width: 15, height: 1.5, background: "rgba(203,213,225,0.7)", borderRadius: 2, transition: "all 0.2s", opacity: menuOpen ? 0 : 1 }} />
            <span style={{ display: "block", width: 15, height: 1.5, background: "rgba(203,213,225,0.7)", borderRadius: 2, transition: "all 0.2s", transform: menuOpen ? "rotate(-45deg) translate(4px, -4px)" : "none" }} />
          </button>
        )}
      </div>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div
          style={{
            borderTop: "1px solid rgba(255,255,255,0.06)",
            background: "rgba(3,4,10,0.95)",
            padding: "12px 28px 16px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {navLinks.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              onClick={() => setMenuOpen(false)}
              style={{
                padding: "9px 12px",
                borderRadius: 8,
                fontSize: 14,
                fontFamily: "var(--font-display)",
                fontWeight: isActive(to) ? 600 : 400,
                color: isActive(to) ? "#e2e8f0" : "rgba(148,163,184,0.7)",
                background: isActive(to) ? "rgba(255,255,255,0.07)" : "transparent",
                textDecoration: "none",
              }}
            >
              {label}
            </Link>
          ))}

          <div style={{ height: 1, background: "rgba(255,255,255,0.06)", margin: "8px 0" }} />

          {user ? (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {streak !== null && (
                    <span style={{ fontSize: 12.5, color: streak > 0 ? "#fb923c" : "rgba(148,163,184,0.45)", fontFamily: "var(--font-display)", display: "flex", alignItems: "center", gap: 3 }}>
                      <span style={{ filter: streak > 0 ? "none" : "grayscale(1)", opacity: streak > 0 ? 1 : 0.5 }}>🔥</span>{streak}
                    </span>
                  )}
                  <span style={{ fontSize: 13, color: "rgba(203,213,225,0.8)", fontFamily: "var(--font-display)" }}>
                    {user.name.split(" ")[0]}
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  style={{ padding: "6px 14px", borderRadius: 8, fontSize: 13, fontFamily: "var(--font-display)", color: "#f87171", background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.15)", cursor: "pointer" }}
                >
                  Sign out
                </button>
              </div>
              {!leetcodeConnected && onConnectLeetCode && (
                <button
                  onClick={() => { setMenuOpen(false); onConnectLeetCode(); }}
                  style={{ margin: "4px 12px 0", padding: "9px 12px", borderRadius: 8, fontSize: 13, fontFamily: "var(--font-display)", fontWeight: 600, color: "#60a5fa", background: "rgba(96,165,250,0.07)", border: "1px solid rgba(96,165,250,0.2)", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 8 }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                  Connect LeetCode
                </button>
              )}
              {leetcodeConnected && onConnectLeetCode && (
                <button
                  onClick={() => { setMenuOpen(false); onConnectLeetCode(); }}
                  style={{ margin: "4px 12px 0", padding: "9px 12px", borderRadius: 8, fontSize: 13, fontFamily: "var(--font-display)", fontWeight: 600, color: "#2dd4bf", background: "rgba(45,212,191,0.07)", border: "1px solid rgba(45,212,191,0.2)", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 8 }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10" />
                    <polyline points="1 20 1 14 7 14" />
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                  </svg>
                  Sync LeetCode
                </button>
              )}
            </>
          ) : (
            <p style={{ fontSize: 12, color: "rgba(100,116,139,0.5)", fontFamily: "var(--font-display)", padding: "4px 12px", margin: 0 }}>
              Sign in below to track progress
            </p>
          )}
        </div>
      )}
    </nav>
  );
}

export default Navbar;
